import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { NAV_LINKS, LOGO } from "@/lib/content";

const Logo = () => (
  <Link to="/" data-testid="logo" className="flex items-center group">
    <img
      src={LOGO}
      alt="Home Cart — Everything for Every Home"
      className="h-[76px] w-auto transition-transform group-hover:scale-105"
    />
  </Link>
);

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToHash = (e, href) => {
    e.preventDefault();
    setOpen(false);
    const id = href.replace("#", "");
    if (location.pathname !== "/") {
      navigate("/");
      let tries = 0;
      const t = setInterval(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
          clearInterval(t);
        } else if (++tries > 40) {
          clearInterval(t);
        }
      }, 50);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 ${
        scrolled
          ? "bg-white border-b border-[#E5E5E5]"
          : "bg-white border-b border-[#E5E5E5]"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex h-[72px] items-center justify-between">
          <Logo />

          <nav className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((link) => {
              const cls =
                "font-body text-sm font-medium text-[#1A1A1A] hover:text-[#F8B808] transition-colors";
              const testid = `nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`;
              return link.href.startsWith("/") ? (
                <Link key={link.label} to={link.href} data-testid={testid} className={cls}>
                  {link.label}
                </Link>
              ) : (
                <a key={link.label} href={link.href} onClick={(e) => goToHash(e, link.href)} data-testid={testid} className={cls}>
                  {link.label}
                </a>
              );
            })}
          </nav>

          <div className="hidden lg:block">
            <a
              href="#contact"
              data-testid="header-cta"
              className="inline-flex items-center justify-center rounded-[5px] bg-[#F8B808] px-4 h-10 font-body text-sm font-medium text-[#1A1A1A] transition-all hover:bg-[#DD9F00] active:scale-[0.98]"
            >
              Request Catalog
            </a>
          </div>

          <button
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-lg text-[#1F2937]"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#E5E7EB] px-5 py-4" data-testid="mobile-menu">
          <div className="flex flex-col gap-1">
            {NAV_LINKS.map((link) => {
              const cls =
                "font-body text-base font-semibold text-[#1F2937] py-3 border-b border-[#F5F5F5]";
              return link.href.startsWith("/") ? (
                <Link key={link.label} to={link.href} onClick={() => setOpen(false)} className={cls}>
                  {link.label}
                </Link>
              ) : (
                <a key={link.label} href={link.href} onClick={(e) => goToHash(e, link.href)} className={cls}>
                  {link.label}
                </a>
              );
            })}
            <a
              href="#contact"
              onClick={() => setOpen(false)}
              className="mt-3 inline-flex items-center justify-center rounded-[5px] bg-[#F8B808] px-5 h-10 font-body text-sm font-medium text-[#1A1A1A]"
            >
              Request Catalog
            </a>
          </div>
        </div>
      )}
    </header>
  );
};
